import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, FormControl, InputLabel, Select, MenuItem,
    Alert, CircularProgress, TextField
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { ReservationService } from '../api/services/reservationService';
import { RoomService } from '../api/services/roomService';

export default function MoveRoomModal({ open, onClose, reservation, tenantId, onSuccess, setNotification }) {
    const [rooms, setRooms] = useState([]);
    const [selectedRoomId, setSelectedRoomId] = useState('');
    const [reason, setReason] = useState('');
    const [isLoadingRooms, setIsLoadingRooms] = useState(false);
    const [isMoving, setIsMoving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open && reservation) {
            setSelectedRoomId('');
            setReason('');
            setError(null);
            fetchAvailableRooms();
        }
    }, [open, reservation]);

    const fetchAvailableRooms = async () => {
        setIsLoadingRooms(true);
        try {
            // Ideally we pass checkIn/checkOut dates if the backend supports it.
            // Using standard RoomService.getAll as requested by existing inventory endpoints.
            const response = await RoomService.getAll({ noPagination: true }, tenantId);
            const allRooms = response.data || response || [];
            
            // Filter out the current room
            const currentRoomId = reservation.roomId || reservation.room?.id;
            const availableRooms = allRooms.filter(r => r.id !== currentRoomId && r.status === 'Available');
            setRooms(availableRooms);
        } catch (err) {
            console.error('Failed to load rooms:', err);
            setError('Could not load available rooms.');
        } finally {
            setIsLoadingRooms(false);
        }
    };

    const handleSave = async () => {
        if (!selectedRoomId) {
            setError('Please select a new target room to proceed.');
            return;
        }
        if (!reason.trim()) {
            setError('An operational reason is required for room transfers.');
            return;
        }

        setIsMoving(true);
        setError(null);
        try {
            // Extract the correct child allocation ID (ReservationRoom.id)
            // If it comes from the master list view, it will have a 'rooms' array.
            const targetId = reservation.rooms && reservation.rooms.length > 0 
                ? reservation.rooms[0].id 
                : reservation.id;

            await ReservationService.moveRoom(targetId, tenantId, {
                newRoomId: selectedRoomId,
                reason: reason.trim()
            });
            if (setNotification) {
                setNotification({ open: true, message: 'Guest successfully reassigned to new room', severity: 'success' });
            }
            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to move reservation room';
            if (setNotification) {
                setNotification({ open: true, message: msg, severity: 'error' });
            } else {
                setError(msg);
            }
        } finally {
            setIsMoving(false);
        }
    };

    if (!reservation) return null;

    const currentRoomDisplay = reservation.room?.roomNumber || reservation.roomNumber || 'Unassigned';

    return (
        <Dialog 
            open={open} 
            onClose={isMoving ? undefined : onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, padding: 1 } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                <SwapHorizIcon color="primary" /> Change / Move Room
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

                <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Current Assignment</Typography>
                    <Typography variant="h6" fontWeight="bold">Room {currentRoomDisplay}</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Guest: {reservation.guestName || reservation.guest?.name || 'Unknown'}
                    </Typography>
                </Box>

                {isLoadingRooms ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Available Target Room</InputLabel>
                            <Select
                                value={selectedRoomId}
                                label="Available Target Room"
                                onChange={(e) => setSelectedRoomId(e.target.value)}
                                disabled={isMoving}
                            >
                                {rooms.map(room => (
                                    <MenuItem key={room.id} value={room.id}>
                                        Room {room.roomNumber} {(room.roomType?.name || room.type) ? `(${room.roomType?.name || room.type})` : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Operational Reason"
                            placeholder="e.g. Guest requested a higher floor with a better view."
                            multiline
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            fullWidth
                            size="small"
                            disabled={isMoving}
                            required
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px' }}>
                <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }} disabled={isMoving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 2 }}
                    disabled={isMoving || !selectedRoomId || !reason.trim()}
                >
                    {isMoving ? <CircularProgress size={20} color="inherit" /> : 'Confirm Move'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
